import { DECK } from './deck';

export interface DrawnCard {
    id: string;
    reversed: boolean;
}

/** A saved reading. Only card ids are stored, so it is shown in whatever language the app is in now. */
export interface Reading {
    id: string;
    spread: import('./spreads').SpreadId;
    /** Milliseconds since the epoch. */
    at: number;
    /** The person a love reading is about. */
    name?: string;
    /** The reader's own name when known, so the longer spread texts can address them. */
    me?: string;
    cards: DrawnCard[];
    /** False while the cards are drawn but the video has not been watched. */
    unlocked: boolean;
    /** Picks the reflection question; saved so it does not change on reopening. */
    seed: number;
}

/** Cards come out reversed about one time in three, as in a hand-shuffled deck. */
export const REVERSED_CHANCE = 0.35;

/** A uniform integer in [0, max) from the platform's secure generator. */
export const randomInt = (max: number): number => {
    const limit = Math.floor(0x100000000 / max) * max;
    const buf = new Uint32Array(1);
    do {
        crypto.getRandomValues(buf);
    } while (buf[0] >= limit);
    return buf[0] % max;
};

/** The whole deck in a random order (Fisher-Yates): the face-down spread the person picks from. */
export const shuffledDeck = (
    random: (max: number) => number = randomInt
): string[] => {
    const ids = DECK.map((c) => c.id);
    for (let i = ids.length - 1; i > 0; i--) {
        const j = random(i + 1);
        [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    return ids;
};

/** Turns the chosen card ids into drawn cards, each upright or reversed by chance. */
export const dealCards = (
    ids: string[],
    random: (max: number) => number = randomInt
): DrawnCard[] =>
    ids.map((id) => ({
        id,
        reversed: random(1000) < REVERSED_CHANCE * 1000,
    }));

/** A random draw of `count` distinct cards (never a repeat within one reading). */
export const drawCards = (
    count: number,
    random: (max: number) => number = randomInt
): DrawnCard[] => dealCards(shuffledDeck(random).slice(0, count), random);

export const newReadingId = (): string =>
    `${Date.now().toString(36)}-${randomInt(0x7fffffff).toString(36)}`;

/** The local calendar day, e.g. "2026-10-06". The daily card resets when this changes (local midnight). */
export const dayKey = (date: Date = new Date()): string => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
};

/** Milliseconds from `now` to the next local midnight (handles daylight-saving days). */
export const msUntilMidnight = (now: Date = new Date()): number =>
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() -
    now.getTime();
